// frontend/src/components/Portfolio/PortfolioBenchmark.tsx
// Portfolio-level benchmarking and peer comparison
// - Compare portfolio to industry averages
// - Percentile rankings vs similar portfolios
// - Regional and sector-based comparisons

import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Link,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Business as BusinessIcon,
  Public as RegionIcon,
  Assessment as AssessmentIcon,
  Info as InfoIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  CompareArrows as CompareIcon,
  Source as SourceIcon,
  Update as UpdateIcon,
} from '@mui/icons-material';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  Cell,
} from 'recharts';

// Import benchmark data from config
import {
  BENCHMARK_METADATA,
  INDUSTRY_BENCHMARKS,
  REGIONAL_BENCHMARKS,
  PERCENTILE_THRESHOLDS,
  DATA_SOURCES,
  SOURCE_URLS,
  IndustryBenchmark,
  RegionalBenchmark,
} from '../../config/benchmarkData';

// ============================================================================
// INTERFACES
// ============================================================================
interface PortfolioItem {
  postal_code?: string;
  city?: string;
  state?: string;
  asset_value?: number | string;
  risk_score?: number;
  resilience?: number;
  com_risk?: number;
  flood?: number;
  wildfire?: number;
  hurricane?: number;
  earthquake?: number;
  heat?: number;
  drought?: number;
  tornado?: number;
  [key: string]: any;
}

interface PortfolioBenchmarkProps {
  portfolio: {
    items: PortfolioItem[];
    name?: string;
    total_value?: number;
    [key: string]: any;
  };
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================
const normalizeAssetValue = (value: any): number => {
  if (!value) return 0;
  if (typeof value === 'number') return value;
  const cleaned = String(value).replace(/[$,]/g, '');
  return parseFloat(cleaned) || 0;
};

const formatCurrency = (value: number): string => {
  if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
  if (value >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
};

const getPercentile = (value: number, thresholds: { p10: number; p25: number; p50: number; p75: number; p90: number }, inverse: boolean = false): number => {
  const { p10, p25, p50, p75, p90 } = thresholds;
  
  let percentile: number;
  if (value <= p10) percentile = 5 + (value / p10) * 5;
  else if (value <= p25) percentile = 10 + ((value - p10) / (p25 - p10)) * 15;
  else if (value <= p50) percentile = 25 + ((value - p25) / (p50 - p25)) * 25;
  else if (value <= p75) percentile = 50 + ((value - p50) / (p75 - p50)) * 25;
  else if (value <= p90) percentile = 75 + ((value - p75) / (p90 - p75)) * 15;
  else percentile = 90 + Math.min(10, ((value - p90) / p90) * 10);
  
  return inverse ? 100 - percentile : percentile;
};

const getPercentileLabel = (percentile: number): string => {
  if (percentile >= 90) return 'Top 10%';
  if (percentile >= 75) return 'Top 25%';
  if (percentile >= 50) return 'Above Average';
  if (percentile >= 25) return 'Below Average';
  return 'Bottom 25%';
};

const getPercentileColor = (percentile: number, goodIsHigh: boolean = true): string => {
  const effective = goodIsHigh ? percentile : 100 - percentile;
  if (effective >= 75) return '#10B981';
  if (effective >= 50) return '#F59E0B';
  if (effective >= 25) return '#F97316';
  return '#EF4444';
};

const getComparisonIcon = (portfolioValue: number, benchmarkValue: number, lowerIsBetter: boolean = false) => {
  const better = lowerIsBetter ? portfolioValue < benchmarkValue : portfolioValue > benchmarkValue;
  const worse = lowerIsBetter ? portfolioValue > benchmarkValue : portfolioValue < benchmarkValue;
  
  if (better) return <TrendingUpIcon sx={{ color: '#10B981', fontSize: 18 }} />;
  if (worse) return <TrendingDownIcon sx={{ color: '#EF4444', fontSize: 18 }} />;
  return <CompareIcon sx={{ color: '#6B7280', fontSize: 18 }} />;
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export default function PortfolioBenchmark({ portfolio }: PortfolioBenchmarkProps) {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedIndustry, setSelectedIndustry] = useState<string>('reit_residential');
  const [selectedRegion, setSelectedRegion] = useState<string>('');
  const [sourcesDialogOpen, setSourcesDialogOpen] = useState(false);
  
  const items = portfolio?.items || [];
  
  // ============================================================================
  // CALCULATE PORTFOLIO METRICS
  // ============================================================================
  const portfolioMetrics = useMemo(() => {
    if (items.length === 0) return null;
    
    const totalValue = items.reduce((sum, item) => sum + normalizeAssetValue(item.asset_value), 0);
    const avgRisk = items.reduce((sum, item) => sum + parseFloat(String(item.risk_score || 0)), 0) / items.length;
    const avgResilience = items.reduce((sum, item) => sum + parseFloat(String(item.resilience || 5)), 0) / items.length;
    
    const highRiskCount = items.filter(item => parseFloat(String(item.risk_score || 0)) > 6).length;
    const highRiskPct = (highRiskCount / items.length) * 100;
    
    // Geographic concentration (HHI)
    const stateValues: Record<string, number> = {};
    items.forEach(item => {
      const state = item.state || 'Unknown';
      const value = normalizeAssetValue(item.asset_value);
      stateValues[state] = (stateValues[state] || 0) + value;
    });
    const totalValueForHHI = Object.values(stateValues).reduce((a, b) => a + b, 0);
    const hhi = Object.values(stateValues).reduce((sum, value) => {
      const share = (value / totalValueForHHI) * 100;
      return sum + share * share;
    }, 0);
    
    // Hazard exposures
    const hazardExposure = {
      flood: items.reduce((sum, item) => sum + parseFloat(String(item.flood || 0)), 0) / items.length,
      wildfire: items.reduce((sum, item) => sum + parseFloat(String(item.wildfire || 0)), 0) / items.length,
      hurricane: items.reduce((sum, item) => sum + parseFloat(String(item.hurricane || 0)), 0) / items.length,
      earthquake: items.reduce((sum, item) => sum + parseFloat(String(item.earthquake || 0)), 0) / items.length,
      heat: items.reduce((sum, item) => sum + parseFloat(String(item.heat || 0)), 0) / items.length,
      drought: items.reduce((sum, item) => sum + parseFloat(String(item.drought || 0)), 0) / items.length,
    };
    
    // Determine primary region
    const regionCounts: Record<string, number> = {};
    items.forEach(item => {
      const state = item.state;
      if (state) {
        const region = REGIONAL_BENCHMARKS.find(r => r.states.includes(state))?.region || 'Unknown';
        regionCounts[region] = (regionCounts[region] || 0) + 1;
      }
    });
    const primaryRegion = Object.entries(regionCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'Unknown';
    
    return {
      totalValue,
      totalLocations: items.length,
      avgRisk,
      avgResilience,
      highRiskCount,
      highRiskPct,
      geographicConcentration: hhi,
      hazardExposure,
      primaryRegion,
      stateCount: Object.keys(stateValues).length,
    };
  }, [items]);
  
  // ============================================================================
  // PERCENTILE RANKINGS
  // ============================================================================
  const percentileRankings = useMemo(() => {
    if (!portfolioMetrics) return null;
    
    return {
      risk: getPercentile(portfolioMetrics.avgRisk, PERCENTILE_THRESHOLDS.risk, true), // Lower risk = higher percentile
      resilience: getPercentile(portfolioMetrics.avgResilience, PERCENTILE_THRESHOLDS.resilience),
      diversification: getPercentile(portfolioMetrics.geographicConcentration, PERCENTILE_THRESHOLDS.diversification, true), // Lower HHI = better
      highRiskPct: getPercentile(portfolioMetrics.highRiskPct, PERCENTILE_THRESHOLDS.highRiskPct, true), // Lower % = better
    };
  }, [portfolioMetrics]);
  
  // ============================================================================
  // INDUSTRY COMPARISON
  // ============================================================================
  const industryComparison = useMemo(() => {
    if (!portfolioMetrics) return null;
    
    const benchmark = INDUSTRY_BENCHMARKS.find(b => b.id === selectedIndustry);
    if (!benchmark) return null;
    
    return {
      benchmark,
      comparisons: {
        risk: {
          portfolio: portfolioMetrics.avgRisk,
          benchmark: benchmark.avgRisk,
          diff: portfolioMetrics.avgRisk - benchmark.avgRisk,
          better: portfolioMetrics.avgRisk < benchmark.avgRisk,
        },
        resilience: {
          portfolio: portfolioMetrics.avgResilience,
          benchmark: benchmark.avgResilience,
          diff: portfolioMetrics.avgResilience - benchmark.avgResilience,
          better: portfolioMetrics.avgResilience > benchmark.avgResilience,
        },
        highRiskPct: {
          portfolio: portfolioMetrics.highRiskPct,
          benchmark: benchmark.highRiskPct,
          diff: portfolioMetrics.highRiskPct - benchmark.highRiskPct,
          better: portfolioMetrics.highRiskPct < benchmark.highRiskPct,
        },
        diversification: {
          portfolio: portfolioMetrics.geographicConcentration,
          benchmark: benchmark.geographicConcentration,
          diff: portfolioMetrics.geographicConcentration - benchmark.geographicConcentration,
          better: portfolioMetrics.geographicConcentration < benchmark.geographicConcentration,
        },
      },
    };
  }, [portfolioMetrics, selectedIndustry]);
  
  // ============================================================================
  // REGIONAL COMPARISON
  // ============================================================================
  const regionalComparison = useMemo(() => {
    if (!portfolioMetrics) return null;
    
    const regionId = selectedRegion || portfolioMetrics.primaryRegion;
    const benchmark = REGIONAL_BENCHMARKS.find(r => r.region === regionId);
    if (!benchmark) return null;
    
    return {
      benchmark,
      comparisons: {
        risk: {
          portfolio: portfolioMetrics.avgRisk,
          benchmark: benchmark.avgRisk,
          diff: portfolioMetrics.avgRisk - benchmark.avgRisk,
          better: portfolioMetrics.avgRisk < benchmark.avgRisk,
        },
        resilience: {
          portfolio: portfolioMetrics.avgResilience,
          benchmark: benchmark.avgResilience,
          diff: portfolioMetrics.avgResilience - benchmark.avgResilience,
          better: portfolioMetrics.avgResilience > benchmark.avgResilience,
        },
      },
    };
  }, [portfolioMetrics, selectedRegion]);
  
  // ============================================================================
  // RADAR CHART DATA
  // ============================================================================
  const radarData = useMemo(() => {
    if (!portfolioMetrics || !industryComparison) return [];
    
    const benchmark = industryComparison.benchmark;
    
    return [
      { metric: 'Risk Score', portfolio: 10 - portfolioMetrics.avgRisk, benchmark: 10 - benchmark.avgRisk, fullMark: 10 },
      { metric: 'Resilience', portfolio: portfolioMetrics.avgResilience, benchmark: benchmark.avgResilience, fullMark: 10 },
      { metric: 'Diversification', portfolio: Math.max(0, 10 - (portfolioMetrics.geographicConcentration / 1000)), benchmark: Math.max(0, 10 - (benchmark.geographicConcentration / 1000)), fullMark: 10 },
      { metric: 'Low High-Risk %', portfolio: Math.max(0, 10 - (portfolioMetrics.highRiskPct / 5)), benchmark: Math.max(0, 10 - (benchmark.highRiskPct / 5)), fullMark: 10 },
      { metric: 'Flood Safety', portfolio: 10 - portfolioMetrics.hazardExposure.flood, benchmark: 10 - benchmark.hazardExposure.flood, fullMark: 10 },
      { metric: 'Fire Safety', portfolio: 10 - portfolioMetrics.hazardExposure.wildfire, benchmark: 10 - benchmark.hazardExposure.wildfire, fullMark: 10 },
    ];
  }, [portfolioMetrics, industryComparison]);
  
  // ============================================================================
  // OVERALL SCORE
  // ============================================================================
  const overallScore = useMemo(() => {
    if (!percentileRankings) return 0;
    
    return Math.round(
      (percentileRankings.risk * 0.35) +
      (percentileRankings.resilience * 0.25) +
      (percentileRankings.diversification * 0.20) +
      (percentileRankings.highRiskPct * 0.20)
    );
  }, [percentileRankings]);
  
  // ============================================================================
  // RENDER
  // ============================================================================
  if (items.length === 0 || !portfolioMetrics) {
    return (
      <Alert severity="info">
        No portfolio data available for benchmarking. Add locations to compare against industry peers.
      </Alert>
    );
  }
  
  const renderStars = (percentile: number) => {
    const stars = percentile >= 90 ? 5 : percentile >= 75 ? 4 : percentile >= 50 ? 3 : percentile >= 25 ? 2 : 1;
    return (
      <Box sx={{ display: 'flex' }}>
        {[1, 2, 3, 4, 5].map(i => (
          i <= stars 
            ? <StarIcon key={i} sx={{ color: '#F59E0B', fontSize: 18 }} />
            : <StarBorderIcon key={i} sx={{ color: '#D1D5DB', fontSize: 18 }} />
        ))}
      </Box>
    );
  };
  
  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Portfolio Benchmarking & Peer Comparison
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Compare your portfolio against industry peers and regional benchmarks
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip 
            icon={<UpdateIcon />}
            label={`Updated: ${BENCHMARK_METADATA.lastUpdated}`}
            size="small"
            variant="outlined"
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<SourceIcon />}
            onClick={() => setSourcesDialogOpen(true)}
          >
            View Sources
          </Button>
        </Box>
      </Box>

      {/* Overall Score Card */}
      <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #F0F9FF 0%, #E0F2FE 100%)' }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{
                width: 120,
                height: 120,
                borderRadius: '50%',
                border: `6px solid ${getPercentileColor(overallScore)}`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                bgcolor: 'white',
              }}>
                <Typography variant="h3" fontWeight={800} sx={{ color: getPercentileColor(overallScore), lineHeight: 1 }}>
                  {overallScore}
                </Typography>
                <Typography variant="caption" color="text.secondary">percentile</Typography>
              </Box>
              <Chip 
                icon={<TrophyIcon />}
                label={getPercentileLabel(overallScore)}
                sx={{ mt: 1, bgcolor: `${getPercentileColor(overallScore)}20`, color: getPercentileColor(overallScore) }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={9}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Your Portfolio Ranking
            </Typography>
            <Grid container spacing={2}>
              {percentileRankings && Object.entries(percentileRankings).map(([key, value]) => {
                const labels: Record<string, string> = {
                  risk: 'Risk Management',
                  resilience: 'Community Resilience',
                  diversification: 'Geographic Diversification',
                  highRiskPct: 'Low Risk Concentration',
                };
                return (
                  <Grid item xs={6} sm={3} key={key}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {labels[key]}
                      </Typography>
                      <Typography variant="h5" fontWeight={700} sx={{ color: getPercentileColor(value) }}>
                        {Math.round(value)}%
                      </Typography>
                      {renderStars(value)}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Industry Comparison" icon={<BusinessIcon />} iconPosition="start" />
          <Tab label="Regional Benchmark" icon={<RegionIcon />} iconPosition="start" />
          <Tab label="Detailed Metrics" icon={<AssessmentIcon />} iconPosition="start" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* Tab 0: Industry Comparison */}
          {activeTab === 0 && (
            <Box>
              <FormControl sx={{ mb: 3, minWidth: 300 }}>
                <InputLabel>Compare Against Industry</InputLabel>
                <Select
                  value={selectedIndustry}
                  onChange={(e) => setSelectedIndustry(e.target.value)}
                  label="Compare Against Industry"
                >
                  {INDUSTRY_BENCHMARKS.map(benchmark => (
                    <MenuItem key={benchmark.id} value={benchmark.id}>
                      <Box>
                        <Typography variant="body2">{benchmark.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{benchmark.description}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {industryComparison && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Performance vs {industryComparison.benchmark.name}
                    </Typography>
                    <Box sx={{ height: 300 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <RadarChart data={radarData}>
                          <PolarGrid />
                          <PolarAngleAxis dataKey="metric" />
                          <PolarRadiusAxis angle={30} domain={[0, 10]} />
                          <Radar name="Your Portfolio" dataKey="portfolio" stroke="#00D4FF" fill="#00D4FF" fillOpacity={0.3} />
                          <Radar name="Industry Avg" dataKey="benchmark" stroke="#6B7280" fill="#6B7280" fillOpacity={0.1} />
                          <Legend />
                        </RadarChart>
                      </ResponsiveContainer>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Metric Comparison
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#F9FAFB' }}>
                            <TableCell>Metric</TableCell>
                            <TableCell align="right">Your Portfolio</TableCell>
                            <TableCell align="right">Industry Avg</TableCell>
                            <TableCell align="center">Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell>Average Risk Score</TableCell>
                            <TableCell align="right">{industryComparison.comparisons.risk.portfolio.toFixed(1)}</TableCell>
                            <TableCell align="right">{industryComparison.comparisons.risk.benchmark.toFixed(1)}</TableCell>
                            <TableCell align="center">
                              {getComparisonIcon(industryComparison.comparisons.risk.portfolio, industryComparison.comparisons.risk.benchmark, true)}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Community Resilience</TableCell>
                            <TableCell align="right">{industryComparison.comparisons.resilience.portfolio.toFixed(1)}</TableCell>
                            <TableCell align="right">{industryComparison.comparisons.resilience.benchmark.toFixed(1)}</TableCell>
                            <TableCell align="center">
                              {getComparisonIcon(industryComparison.comparisons.resilience.portfolio, industryComparison.comparisons.resilience.benchmark)}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>High-Risk Locations %</TableCell>
                            <TableCell align="right">{industryComparison.comparisons.highRiskPct.portfolio.toFixed(1)}%</TableCell>
                            <TableCell align="right">{industryComparison.comparisons.highRiskPct.benchmark.toFixed(1)}%</TableCell>
                            <TableCell align="center">
                              {getComparisonIcon(industryComparison.comparisons.highRiskPct.portfolio, industryComparison.comparisons.highRiskPct.benchmark, true)}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>
                              Geographic Concentration
                              <Tooltip title="Lower HHI = more diversified">
                                <IconButton size="small"><InfoIcon fontSize="small" /></IconButton>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="right">{industryComparison.comparisons.diversification.portfolio.toFixed(0)}</TableCell>
                            <TableCell align="right">{industryComparison.comparisons.diversification.benchmark.toFixed(0)}</TableCell>
                            <TableCell align="center">
                              {getComparisonIcon(industryComparison.comparisons.diversification.portfolio, industryComparison.comparisons.diversification.benchmark, true)}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        <strong>{industryComparison.benchmark.name}</strong> typical portfolio size: {industryComparison.benchmark.typicalSize}
                      </Typography>
                    </Alert>
                  </Grid>
                </Grid>
              )}
            </Box>
          )}

          {/* Tab 1: Regional Benchmark */}
          {activeTab === 1 && (
            <Box>
              <FormControl sx={{ mb: 3, minWidth: 250 }}>
                <InputLabel>Region</InputLabel>
                <Select
                  value={selectedRegion || portfolioMetrics.primaryRegion}
                  onChange={(e) => setSelectedRegion(e.target.value)}
                  label="Region"
                >
                  {REGIONAL_BENCHMARKS.map(region => (
                    <MenuItem key={region.region} value={region.region}>
                      {region.region} ({region.states.length} states)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  Your portfolio is primarily located in the <strong>{portfolioMetrics.primaryRegion}</strong> region 
                  ({portfolioMetrics.stateCount} states represented)
                </Typography>
              </Alert>

              <Grid container spacing={3}>
                {REGIONAL_BENCHMARKS.map(region => {
                  const isYourRegion = region.region === portfolioMetrics.primaryRegion;
                  const riskComparison = portfolioMetrics.avgRisk < region.avgRisk;
                  
                  return (
                    <Grid item xs={12} sm={6} md={4} key={region.region}>
                      <Card sx={{ 
                        height: '100%',
                        border: isYourRegion ? '2px solid #00D4FF' : '1px solid #E5E7EB',
                        bgcolor: isYourRegion ? '#F0F9FF' : 'white',
                      }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {region.region}
                              {isYourRegion && <Chip label="Your Region" size="small" sx={{ ml: 1 }} color="primary" />}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">Avg Risk:</Typography>
                            <Typography variant="body2" fontWeight={600}>{region.avgRisk.toFixed(1)}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">Avg Resilience:</Typography>
                            <Typography variant="body2" fontWeight={600}>{region.avgResilience.toFixed(1)}</Typography>
                          </Box>
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">Top Hazards:</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                              {region.topHazards.map(hazard => (
                                <Chip key={hazard} label={hazard} size="small" variant="outlined" />
                              ))}
                            </Box>
                          </Box>
                          
                          {isYourRegion && (
                            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #E5E7EB' }}>
                              <Typography variant="caption" color="text.secondary">
                                Your portfolio vs region:
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                {riskComparison ? (
                                  <Chip 
                                    icon={<TrendingUpIcon />} 
                                    label={`${(region.avgRisk - portfolioMetrics.avgRisk).toFixed(1)} lower risk`}
                                    size="small"
                                    color="success"
                                  />
                                ) : (
                                  <Chip 
                                    icon={<TrendingDownIcon />} 
                                    label={`${(portfolioMetrics.avgRisk - region.avgRisk).toFixed(1)} higher risk`}
                                    size="small"
                                    color="error"
                                  />
                                )}
                              </Box>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}

          {/* Tab 2: Detailed Metrics */}
          {activeTab === 2 && (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Hazard Exposure Comparison
                  </Typography>
                  <Box sx={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={[
                          { hazard: 'Flood', portfolio: parseFloat(portfolioMetrics.hazardExposure.flood.toFixed(1)), industry: industryComparison?.benchmark.hazardExposure.flood || 0 },
                          { hazard: 'Wildfire', portfolio: parseFloat(portfolioMetrics.hazardExposure.wildfire.toFixed(1)), industry: industryComparison?.benchmark.hazardExposure.wildfire || 0 },
                          { hazard: 'Hurricane', portfolio: parseFloat(portfolioMetrics.hazardExposure.hurricane.toFixed(1)), industry: industryComparison?.benchmark.hazardExposure.hurricane || 0 },
                          { hazard: 'Earthquake', portfolio: parseFloat(portfolioMetrics.hazardExposure.earthquake.toFixed(1)), industry: industryComparison?.benchmark.hazardExposure.earthquake || 0 },
                          { hazard: 'Heat', portfolio: parseFloat(portfolioMetrics.hazardExposure.heat.toFixed(1)), industry: industryComparison?.benchmark.hazardExposure.heat || 0 },
                          { hazard: 'Drought', portfolio: parseFloat(portfolioMetrics.hazardExposure.drought.toFixed(1)), industry: industryComparison?.benchmark.hazardExposure.drought || 0 },
                        ]}
                        layout="vertical"
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" domain={[0, 10]} />
                        <YAxis type="category" dataKey="hazard" width={80} />
                        <RechartsTooltip formatter={(value: number) => value.toFixed(1)} />
                        <Legend />
                        <Bar dataKey="portfolio" name="Your Portfolio" fill="#00D4FF" />
                        <Bar dataKey="industry" name="Industry Avg" fill="#6B7280" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Portfolio Summary
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell>Total Portfolio Value</TableCell>
                          <TableCell align="right"><strong>{formatCurrency(portfolioMetrics.totalValue)}</strong></TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Total Locations</TableCell>
                          <TableCell align="right"><strong>{portfolioMetrics.totalLocations}</strong></TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>States Represented</TableCell>
                          <TableCell align="right"><strong>{portfolioMetrics.stateCount}</strong></TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Average Risk Score</TableCell>
                          <TableCell align="right"><strong>{portfolioMetrics.avgRisk.toFixed(2)}</strong></TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Average Resilience</TableCell>
                          <TableCell align="right"><strong>{portfolioMetrics.avgResilience.toFixed(2)}</strong></TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>High-Risk Locations</TableCell>
                          <TableCell align="right">
                            <strong>{portfolioMetrics.highRiskCount}</strong> ({portfolioMetrics.highRiskPct.toFixed(1)}%)
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            Geographic Concentration (HHI)
                            <Tooltip title="Herfindahl-Hirschman Index: Lower = more diversified. Under 1500 is well diversified.">
                              <IconButton size="small"><InfoIcon fontSize="small" /></IconButton>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right"><strong>{portfolioMetrics.geographicConcentration.toFixed(0)}</strong></TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Primary Region</TableCell>
                          <TableCell align="right"><strong>{portfolioMetrics.primaryRegion}</strong></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Sources Dialog */}
      <Dialog open={sourcesDialogOpen} onClose={() => setSourcesDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SourceIcon />
            Benchmark Data Sources
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Last Updated:</strong> {BENCHMARK_METADATA.lastUpdated} | 
              <strong> Next Review:</strong> {BENCHMARK_METADATA.nextReviewDate} | 
              <strong> Version:</strong> {BENCHMARK_METADATA.version}
            </Typography>
          </Alert>

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Federal Data Sources
          </Typography>
          <List dense>
            {DATA_SOURCES.federal.map((source, i) => (
              <ListItem key={i}>
                <ListItemText 
                  primary={source.name}
                  secondary={
                    <Box component="span">
                      <Link href={source.url} target="_blank" rel="noopener">{source.url}</Link>
                      <Typography component="span" variant="caption" sx={{ ml: 1 }}>
                        (Updates: {source.updateFrequency})
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Industry Data Sources
          </Typography>
          <List dense>
            {DATA_SOURCES.industry.map((source, i) => (
              <ListItem key={i}>
                <ListItemText 
                  primary={source.name}
                  secondary={
                    <Box component="span">
                      <Link href={source.url} target="_blank" rel="noopener">{source.url}</Link>
                      <Typography component="span" variant="caption" sx={{ ml: 1 }}>
                        (Updates: {source.updateFrequency})
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Research Sources
          </Typography>
          <List dense>
            {DATA_SOURCES.research.map((source, i) => (
              <ListItem key={i}>
                <ListItemText 
                  primary={source.name}
                  secondary={
                    <Box component="span">
                      <Link href={source.url} target="_blank" rel="noopener">{source.url}</Link>
                      <Typography component="span" variant="caption" sx={{ ml: 1 }}>
                        (Updates: {source.updateFrequency})
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Selected Industry Benchmark Sources
          </Typography>
          {INDUSTRY_BENCHMARKS.slice(0, 3).map((benchmark) => (
            <Box key={benchmark.id} sx={{ mb: 2, pl: 2 }}>
              <Typography variant="body2" fontWeight={500}>{benchmark.name}</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                {benchmark.sources?.map((source, idx) => {
                  const url = SOURCE_URLS[source];
                  return url ? (
                    <Link 
                      key={idx} 
                      href={url} 
                      target="_blank" 
                      rel="noopener"
                      sx={{ fontSize: '0.75rem' }}
                    >
                      {source}
                    </Link>
                  ) : (
                    <Typography key={idx} variant="caption" color="text.secondary">
                      {source}
                    </Typography>
                  );
                })}
              </Box>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1, pl: 2 }}>
            To update benchmarks, edit: frontend/src/config/benchmarkData.ts
          </Typography>
          <Button onClick={() => setSourcesDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}